import type { SchemaProperty } from 'types/schema';

/**
 * Options for a string property that should render as a dropdown/select, or `null`
 * for a plain text input. Prefers `metadata.options` (used when a label differs from
 * its stored value), otherwise derives them from the `oneOf` validation rule.
 *
 * `oneOf` (Yup/Joi) takes a SINGLE array argument, so it is stored NESTED —
 * `args: [["Warranty","Customer-Pay","Internal"]]`. We therefore read `args[0]`,
 * falling back to a flat `args` defensively. Reading `args` directly renders every
 * value as one comma-joined option (the bug this shared helper exists to prevent —
 * the Add-Sphere dialog and the Sphere editor must derive options identically).
 */
export function getSelectOptions(
  prop: SchemaProperty
): { label: string; value: string }[] | null {
  if (prop.type !== 'string') return null;

  const meta = (prop as { metadata?: { options?: { label: string; value: string }[] } }).metadata;
  if (Array.isArray(meta?.options) && meta.options.length > 0) {
    return meta.options;
  }

  const rawArgs = prop.rules?.find((r) => r.method === 'oneOf')?.args;
  const values = Array.isArray(rawArgs?.[0]) ? rawArgs[0] : rawArgs;
  if (Array.isArray(values) && values.length > 0) {
    return values.map((v: unknown) => ({ label: String(v), value: String(v) }));
  }
  return null;
}
